// ─────────────────────────────────────────────────────────────────────────────
// AVATAR 2 — JPMorgan / Fintech, Banking & Digital Platforms identity
// Content source of truth: public/resumes/avatar2.pdf (Sr. Frontend Developer)
// ─────────────────────────────────────────────────────────────────────────────

const avatar2 = {
  // ── Personal ──────────────────────────────────────────────────────────────
  name: 'Vamsi Krishna Chiguruwada',
  displayName: 'Vamsi Krishna',

  contact: {
    email: 'vamsi@vamsikrish.com',
    phone: '+1 339-242-3758',
    location: 'Boston, MA — USA',
    linkedin:      { label: 'vamsi-krish-c',            url: 'https://www.linkedin.com/in/vamsi-krish-c' },
    github:        { label: 'Vam-si-krish',              url: 'https://github.com/Vam-si-krish' },
    instagram:     'https://instagram.com/vamsi.krish_/',
    stackoverflow: 'https://stackoverflow.com/users/14326771/vamsi-chiguruwada',
    website:       'https://vamsikrish.com',
  },

  // ── Home page ──────────────────────────────────────────────────────────────
  home: {
    roles: [
      'Senior Frontend Developer',
      'React & Next.js Engineer',
      'AI-Powered UI Builder',
      'Frontend Architect',
    ],
    techStack: ['React', 'Next.js', 'TypeScript', 'GraphQL', 'Angular', 'Node.js', 'AWS'],
    tagline: 'Designing and delivering enterprise-scale web applications for 5+ years — across fintech, banking, and digital platforms.',
    status: 'Available for new opportunities',
    currentCompany: { name: 'JPMorgan Chase & Co.' },
    stats: [
      { value: '5+', label: 'Years Exp.' },
      { value: '10+', label: 'Projects' },
      { value: '3', label: 'Companies' },
    ],
  },

  // ── About page ─────────────────────────────────────────────────────────────
  about: {
    bio: 'Senior Frontend Developer with 5+ years designing, architecting, and delivering enterprise-scale web applications across fintech, banking, and digital platforms. Expert in React, Next.js, and TypeScript — building component-driven design systems, micro-frontend solutions, AI-powered user interfaces, and real-time data applications. Authorized to work in the U.S. without sponsorship.',
    skills: [
      { name: 'React / Next.js',       pct: 92, cat: 'Frontend', color: '#5ED4F4' },
      { name: 'TypeScript',            pct: 90, cat: 'Frontend', color: '#6488ea' },
      { name: 'Angular 2+',            pct: 84, cat: 'Frontend', color: '#DD0031' },
      { name: 'CSS / Sass / Tailwind', pct: 88, cat: 'Frontend', color: '#38BDF8' },
      { name: 'Node.js / Express',     pct: 80, cat: 'Backend',  color: '#68A063' },
      { name: 'GraphQL',               pct: 78, cat: 'Backend',  color: '#E535AB' },
      { name: 'Jest & Testing',        pct: 83, cat: 'Backend',  color: '#C21325' },
      { name: 'AWS / CI-CD',           pct: 74, cat: 'Backend',  color: '#FF9900' },
    ],
    education: [
      {
        degree: 'M.S. Business Analytics',
        school: 'Hult International Business School',
        location: 'Cambridge, MA',
        year: '2025',
        stem: true,
      },
      {
        degree: 'B.Tech Computer Science & Engineering',
        school: 'Jawaharlal Nehru Technological University',
        location: 'Anantapur, India',
        year: '2020',
        stem: false,
      },
    ],
    availability: 'Open to new opportunities',
  },

  // ── Experience page ────────────────────────────────────────────────────────
  experience: {
    subtitle: '5+ years delivering enterprise-scale frontend solutions across fintech, banking, and digital platforms',
    jobs: [
      {
        company: 'JPMorgan Chase & Co.',
        initial: 'JP',
        title: 'Senior Frontend Developer',
        range: 'Jul 2025 – Present',
        tenure: 'Current',
        isCurrent: true,
        metrics: [
          { value: '−50%', label: 'Data Latency' },
          { value: '500+', label: 'Platform Users' },
          { value: '−40%', label: 'Page Load' },
        ],
        tags: ['React', 'Next.js', 'TypeScript', 'GraphQL', 'Redux Toolkit', 'Tailwind CSS', 'RAG', 'WebSockets'],
        bullets: [
          { lead: 'Financial Intelligence Platform', detail: 'Architected and delivered a React, Next.js, TypeScript, and GraphQL platform for transaction monitoring, portfolio analytics, and operational reporting — cutting data retrieval latency by 50% and enabling real-time decisions for 500+ operations, risk, and portfolio users.' },
          { lead: 'Enterprise Design System', detail: 'Designed a scalable component-driven architecture with reusable UI libraries, standardized patterns, and centralized state management, reducing code duplication by 35% and accelerating delivery across digital banking and payments products.' },
          { lead: 'Accessible Banking UIs', detail: 'Built responsive, WCAG 2.1 AA-compliant customer-facing banking applications with React, Next.js, Tailwind CSS, and Redux Toolkit, improving accessibility and engagement across desktop and mobile.' },
          { lead: 'Conversational Banking AI', detail: 'Integrated AI-powered banking assistants and LLM-driven financial insights, enabling natural-language access to account activity, transaction histories, and portfolio metrics while increasing self-service adoption.' },
          { lead: 'RAG Application Frontends', detail: 'Developed Retrieval-Augmented Generation experiences surfacing contextual financial documents, compliance policies, and risk knowledge from vector databases — significantly cutting information discovery time.' },
          { lead: 'Performance & Integration', detail: 'Integrated GraphQL, REST, WebSockets, and event-driven architectures with code splitting, lazy loading, and caching strategies that reduced page load times by 40%.' },
        ],
      },
      {
        company: 'Yash Technologies',
        initial: 'YT',
        title: 'Frontend Developer',
        range: 'Sep 2022 – Aug 2024',
        tenure: '2 yrs',
        isCurrent: false,
        metrics: [
          { value: '10K+', label: 'Sessions' },
          { value: '−33%', label: 'Bundle Size' },
          { value: '−15%', label: 'API Failures' },
        ],
        tags: ['Angular 2+', 'TypeScript', 'Redux', 'SASS', 'Webpack', 'Jenkins', 'Docker', 'Jest', 'Selenium'],
        bullets: [
          { lead: 'Digital Banking & Payments', detail: 'Engineered customer-facing banking and payment management applications with Angular, TypeScript, and Redux — supporting 10,000+ concurrent sessions and secure real-time transaction workflows.' },
          { lead: 'Modular Component Library', detail: 'Developed modular, reusable frontend components, improving maintainability and reducing development effort across multiple financial services applications.' },
          { lead: 'Bundle Optimization', detail: 'Optimized frontend performance with Webpack, SASS, and advanced asset management, reducing bundle sizes by 33% and improving responsiveness across browsers and devices.' },
          { lead: 'Auth & Security Refactor', detail: 'Refactored authentication and authorization with JWT, Axios interceptors, and secure session management — cutting API failure rates by 15% and strengthening security.' },
          { lead: 'CI/CD Automation', detail: 'Implemented Jenkins + Docker pipelines with GitHub-based workflows, streamlining deployments and supporting reliable release cycles with minimal downtime.' },
          { lead: 'Testing Strategy', detail: 'Built comprehensive testing with Jest, Selenium, and automated regression suites — improving release quality and reducing production defects.' },
        ],
      },
      {
        company: 'Msys Technologies',
        initial: 'MT',
        title: 'Frontend Developer',
        range: 'Feb 2020 – Aug 2022',
        tenure: '2 yrs 6 mo',
        isCurrent: false,
        metrics: [
          { value: '80%+', label: 'Test Coverage' },
          { value: '4', label: 'Browsers' },
          { value: 'ES6+', label: 'Modernized', type: 'badge' },
        ],
        tags: ['React', 'JavaScript', 'HTML5', 'CSS3', 'REST APIs', 'Jest', 'React Testing Library'],
        bullets: [
          { lead: 'Shared Component Libraries', detail: 'Developed and maintained reusable React component libraries and shared UI frameworks adopted across multiple enterprise applications, ensuring consistent user experiences.' },
          { lead: 'Data-Driven Interfaces', detail: 'Built dynamic UIs with React, JavaScript, HTML5, and CSS3, integrating REST APIs and asynchronous workflows supporting high-volume business operations.' },
          { lead: 'Cross-Browser Engineering', detail: 'Diagnosed and resolved complex rendering and responsiveness issues, improving compatibility across Chrome, Safari, Firefox, and Edge.' },
          { lead: 'Forms & Dashboards', detail: 'Developed interactive forms, dashboards, and workflow management interfaces with robust validation, state management, and async data handling.' },
          { lead: '80%+ Test Coverage', detail: 'Implemented unit and integration testing with Jest and React Testing Library, achieving 80%+ coverage for critical business workflows.' },
          { lead: 'Legacy Modernization', detail: 'Refactored legacy jQuery applications into modern ES6+ React architectures through Agile cycles and code reviews, improving maintainability and productivity.' },
        ],
      },
    ],
    arsenal: [
      { category: 'Frontend',                 tags: ['React.js', 'Next.js', 'TypeScript', 'JavaScript (ES6+)', 'Angular 2+', 'HTML5', 'CSS3'] },
      { category: 'State & Data Layer',       tags: ['Redux Toolkit', 'Context API', 'TanStack Query', 'Apollo Client', 'GraphQL'] },
      { category: 'AI-Enabled Applications',  tags: ['LLM API Integration', 'AI Chat Interfaces', 'RAG Applications', 'AI-Powered Search', 'Prompt-Oriented UI'] },
      { category: 'Architecture',             tags: ['Micro-Frontends', 'Module Federation', 'Design Systems', 'Monorepos (TurboRepo)', 'Component-Driven Development'] },
      { category: 'Testing',                  tags: ['Jest', 'React Testing Library', 'Cypress', 'Playwright', 'Selenium'] },
      { category: 'Cloud & DevOps',           tags: ['AWS (S3, CloudFront, Lambda)', 'Docker', 'Jenkins', 'GitHub Actions', 'CI/CD'] },
      { category: 'Performance',              tags: ['Lighthouse', 'Core Web Vitals', 'Code Splitting', 'Sentry', 'OpenTelemetry'] },
    ],
  },

  // ── Resume page ────────────────────────────────────────────────────────────
  resume: {
    title: 'Senior Frontend Developer',
    summary: 'Senior Frontend Developer with 5+ years of experience designing, architecting, and delivering enterprise-scale web applications across fintech, banking, and digital platforms. Expertise in React.js, Next.js, TypeScript, and modern frontend architectures, with a strong track record of building high-performance, accessible, and scalable user experiences. Experienced in developing component-driven design systems, micro-frontend solutions, AI-powered user interfaces, and real-time data applications that support mission-critical business operations. Proven ability to optimize frontend performance, integrate GraphQL and REST APIs, implement cloud-native CI/CD pipelines, and collaborate with cross-functional teams to deliver secure, resilient, and customer-centric solutions.',
    pdfUrl: null,   // using public/resumes/avatar2.pdf (same-origin, no CORS)
    pdfPath: '/resumes/avatar2.pdf',
    downloadFilename: 'Vamsi_Krishna_Chiguruwada_Resume.pdf',
    skills: [
      {
        category: 'Frontend Technologies',
        items: ['React.js', 'Next.js', 'TypeScript', 'JavaScript (ES6+)', 'Angular (2+)', 'HTML5', 'CSS3'],
      },
      {
        category: 'State Management & Data Layer',
        items: ['Redux Toolkit', 'Context API', 'TanStack Query', 'Apollo Client', 'GraphQL'],
      },
      {
        category: 'AI-Enabled Applications',
        items: ['LLM API Integration', 'AI Chat Interfaces', 'RAG Applications', 'Conversational AI', 'AI-Powered Search', 'Prompt-Oriented UI'],
      },
      {
        category: 'Frontend Architecture',
        items: ['Micro-Frontends', 'Module Federation', 'Component-Driven Development', 'Design Systems', 'Monorepos (TurboRepo)'],
      },
      {
        category: 'UI Engineering',
        items: ['Tailwind CSS', 'Bootstrap', 'SASS', 'Responsive Design', 'Mobile-First', 'WCAG 2.1 AA', 'Cross-Browser'],
      },
      {
        category: 'Backend & API Integration',
        items: ['Node.js', 'Express.js', 'REST APIs', 'GraphQL APIs', 'WebSockets', 'SSR', 'Async Programming'],
      },
      {
        category: 'Build & Development Tools',
        items: ['Vite', 'Webpack', 'Babel', 'NPM', 'Yarn'],
      },
      {
        category: 'Testing & Quality Assurance',
        items: ['Jest', 'React Testing Library', 'Cypress', 'Playwright', 'Selenium', 'Chrome DevTools'],
      },
      {
        category: 'Cloud, DevOps & CI/CD',
        items: ['AWS (S3, CloudFront, Lambda)', 'Docker', 'Jenkins', 'GitHub Actions', 'Git', 'CI/CD Pipelines'],
      },
      {
        category: 'Performance & Observability',
        items: ['Lighthouse', 'Core Web Vitals', 'Code Splitting', 'Lazy Loading', 'Bundle Optimization', 'Sentry', 'OpenTelemetry'],
      },
      {
        category: 'Methodologies',
        items: ['Agile/Scrum', 'SDLC', 'TDD', 'Code Reviews', 'Cross-Functional Collaboration'],
      },
    ],
    experience: [
      {
        company: 'JPMorgan Chase & Co.',
        title: 'Senior Frontend Developer',
        range: 'Jul 2025 – Present',
        bullets: [
          { lead: 'Financial Intelligence Platform', detail: 'Architected and delivered a React.js, Next.js, TypeScript, and GraphQL-based platform supporting transaction monitoring, portfolio analytics, and operational reporting — reducing data retrieval latency by 50% and enabling real-time decision-making for 500+ operations, risk, and portfolio management users.' },
          { lead: 'Enterprise Design System', detail: 'Designed and implemented a scalable component-driven architecture with reusable UI libraries, standardized patterns, and centralized state management — reducing code duplication by 35% and accelerating feature delivery across digital banking and payments products.' },
          { lead: 'Accessible Banking Applications', detail: 'Built responsive, WCAG 2.1 AA-compliant customer-facing banking applications using React, Next.js, Tailwind CSS, and Redux Toolkit, improving accessibility, engagement, and user experience across desktop and mobile.' },
          { lead: 'Conversational Banking AI', detail: 'Integrated AI-powered conversational banking assistants and LLM-driven financial insights, enabling natural-language access to account activity, transaction histories, portfolio metrics, and operational reports while increasing self-service adoption.' },
          { lead: 'RAG Application Frontends', detail: 'Developed frontend experiences for Retrieval-Augmented Generation applications surfacing contextual financial documents, compliance policies, and risk knowledge from vector databases — significantly reducing information discovery time for support and compliance teams.' },
          { lead: 'Performance & Integration', detail: 'Collaborated with backend, data engineering, and risk teams to integrate GraphQL APIs, REST services, WebSockets, and event-driven architectures — with code splitting, lazy loading, and caching strategies reducing page load times by 40%.' },
        ],
      },
      {
        company: 'Yash Technologies',
        title: 'Frontend Developer',
        range: 'Sep 2022 – Aug 2024',
        bullets: [
          { lead: 'Digital Banking & Payments', detail: 'Engineered customer-facing digital banking and payment management applications using Angular, TypeScript, and Redux — supporting 10,000+ concurrent sessions and secure real-time transaction processing workflows.' },
          { lead: 'Modular Components', detail: 'Developed modular and reusable frontend components, improving maintainability and reducing development effort across multiple financial services applications.' },
          { lead: 'Bundle Optimization', detail: 'Optimized frontend performance using Webpack, SASS, and advanced asset management — reducing bundle sizes by 33% and improving responsiveness across browsers and devices.' },
          { lead: 'Auth & Security', detail: 'Refactored authentication and authorization workflows using JWT, Axios interceptors, and secure session management — reducing API failure rates by 15% and strengthening application security.' },
          { lead: 'CI/CD Automation', detail: 'Implemented automated pipelines using Jenkins, Docker, and GitHub-based workflows, streamlining deployments and supporting reliable release cycles with minimal downtime.' },
          { lead: 'Testing Strategy', detail: 'Developed comprehensive testing strategies using Jest, Selenium, and automated regression suites — improving release quality and reducing production defects across critical business applications.' },
        ],
      },
      {
        company: 'Msys Technologies',
        title: 'Frontend Developer',
        range: 'Feb 2020 – Aug 2022',
        bullets: [
          { lead: 'Shared Component Libraries', detail: 'Developed and maintained reusable React component libraries and shared UI frameworks adopted across multiple enterprise applications, improving development efficiency and consistency.' },
          { lead: 'Data-Driven Interfaces', detail: 'Built dynamic, data-driven user interfaces using React, JavaScript, HTML5, and CSS3 — integrating REST APIs and asynchronous workflows for high-volume business operations.' },
          { lead: 'Cross-Browser Engineering', detail: 'Diagnosed and resolved complex rendering and responsiveness issues using Chrome DevTools, improving compatibility across Chrome, Safari, Firefox, and Edge.' },
          { lead: 'Forms & Dashboards', detail: 'Developed interactive forms, dashboards, and workflow management interfaces with robust validation, state management, and asynchronous data handling.' },
          { lead: '80%+ Test Coverage', detail: 'Implemented unit and integration testing using Jest and React Testing Library, achieving over 80% coverage for critical business workflows.' },
          { lead: 'Legacy Modernization', detail: 'Participated in Agile cycles, code reviews, and modernization initiatives — refactoring legacy jQuery applications into modern ES6+ React architectures.' },
        ],
      },
    ],
    education: [
      {
        school: 'Hult International Business School',
        degree: 'M.S. Business Analytics (STEM)',
        location: 'Cambridge, MA',
        range: 'Sep 2024 – Aug 2025',
      },
      {
        school: 'Jawaharlal Nehru Technological University',
        degree: 'B.Tech Computer Science & Engineering',
        location: 'Anantapur, India',
        range: 'Jul 2016 – Aug 2020',
      },
    ],
  },

  // ── AI Chatbot ─────────────────────────────────────────────────────────────
  ai: {
    widgetTitle: "Vamsi's AI",
    placeholder: 'Ask anything about Vamsi…',
    disclaimer: 'AI responses are generated and may not be perfectly accurate.',
    systemPrompt: `You are an AI assistant for Vamsi Krishna Chiguruwada, a Senior Frontend Developer with 5+ years of experience. Answer recruiter and hiring manager questions concisely and accurately using bullet points where appropriate.

SUMMARY:
Senior Frontend Developer with 5+ years designing, architecting, and delivering enterprise-scale web applications across fintech, banking, and digital platforms. Expertise in React.js, Next.js, TypeScript, and modern frontend architectures. Experienced in component-driven design systems, micro-frontend solutions, AI-powered user interfaces (LLM integrations, RAG applications, conversational AI), and real-time data applications. Skilled at performance optimization, GraphQL/REST integration, and cloud-native CI/CD.

EXPERIENCE:
- JPMorgan Chase & Co. (Jul 2025–Present) — Senior Frontend Developer
  • Architected a React/Next.js/TypeScript/GraphQL financial intelligence platform (transaction monitoring, portfolio analytics, operational reporting) — 50% lower data retrieval latency, serving 500+ operations, risk, and portfolio users.
  • Built an enterprise design system with reusable UI libraries and centralized state management — 35% less code duplication across digital banking and payments products.
  • Delivered WCAG 2.1 AA-compliant customer-facing banking apps with React, Next.js, Tailwind CSS, and Redux Toolkit.
  • Integrated AI-powered conversational banking assistants and LLM-driven financial insights for natural-language access to accounts, transactions, and portfolios.
  • Built RAG application frontends surfacing financial documents, compliance policies, and risk knowledge from vector databases.
  • Integrated GraphQL, REST, WebSockets, and event-driven architectures; code splitting, lazy loading, and caching cut page load times by 40%.

- Yash Technologies (Sep 2022–Aug 2024) — Frontend Developer
  • Engineered digital banking and payment applications with Angular, TypeScript, and Redux for 10,000+ concurrent sessions with real-time transaction processing.
  • Reduced bundle sizes by 33% using Webpack, SASS, and advanced asset management.
  • Refactored auth workflows with JWT and Axios interceptors, reducing API failure rates by 15%.
  • Implemented Jenkins + Docker CI/CD pipelines; comprehensive Jest and Selenium testing strategies.

- Msys Technologies (Feb 2020–Aug 2022) — Frontend Developer
  • Built reusable React component libraries and shared UI frameworks used across multiple enterprise applications.
  • Developed data-driven UIs with React and REST APIs; resolved cross-browser issues across Chrome, Safari, Firefox, and Edge.
  • Achieved 80%+ Jest and React Testing Library coverage for critical workflows.
  • Refactored legacy jQuery applications into modern ES6+ React architectures.

TECHNICAL SKILLS:
- Frontend: React.js, Next.js, TypeScript, JavaScript (ES6+), Angular (2+), HTML5, CSS3
- State & Data: Redux Toolkit, Context API, TanStack Query, Apollo Client, GraphQL
- AI-Enabled Apps: LLM API integration, AI chat interfaces, RAG applications, conversational AI, AI-powered search, prompt-oriented UI development
- Architecture: Micro-frontends, Module Federation, component-driven development, design systems, monorepos (TurboRepo)
- UI Engineering: Tailwind CSS, Bootstrap, SASS, responsive/mobile-first design, WCAG 2.1 AA accessibility
- Backend & APIs: Node.js, Express.js, REST, GraphQL, WebSockets, SSR
- Build Tools: Vite, Webpack, Babel, NPM, Yarn
- Testing: Jest, React Testing Library, Cypress, Playwright, Selenium
- Cloud & DevOps: AWS (S3, CloudFront, Lambda), Docker, Jenkins, GitHub Actions, CI/CD
- Performance & Observability: Lighthouse, Core Web Vitals, code splitting, lazy loading, Sentry, OpenTelemetry
- Methodologies: Agile/Scrum, SDLC, TDD, code reviews

EDUCATION:
- M.S. Business Analytics (STEM) — Hult International Business School, Cambridge MA (Sep 2024–Aug 2025)
- B.Tech Computer Science & Engineering — JNTU Anantapur, India (Jul 2016–Aug 2020)

KEY FACTS: U.S. work authorized, no sponsorship required. Based in Boston, MA. Contact: vamsi@vamsikrish.com | vamsikrish.com`,
    initialMessage: "Hi! I'm Vamsi's AI assistant. Ask me about his experience, skills, projects, or education.",
    quickPrompts: [
      { label: 'Skills',     text: 'What are your core technical skills?' },
      { label: 'Experience', text: 'Summarize your work experience.' },
      { label: 'AI Work',    text: 'What AI-powered interfaces have you built?' },
      { label: 'Contact',    text: 'How can I reach you?' },
    ],
    suggestedPrompts: [
      "What are Vamsi's core technical skills and tech stack?",
      "Tell me about Vamsi's work at JPMorgan Chase.",
      'What AI-powered and RAG interfaces has Vamsi built?',
      'How did Vamsi improve frontend performance and accessibility?',
      "What is Vamsi's experience with Angular and React?",
      'How can I get in touch with Vamsi for an interview?',
    ],
    // Shown by the animated avatar as "footnote" bubbles. Each entry:
    //   { text: shown in the bubble, prompt: optional — sent to the AI on click }
    // Delete this array to fall back to facts auto-derived from this profile.
    facts: [
      {
        text: 'Vamsi architected a financial intelligence platform at JPMorgan Chase that cut data-retrieval latency in half for 500+ users.',
        prompt: "Tell me about Vamsi's work at JPMorgan Chase.",
      },
      {
        text: 'He has shipped AI-powered banking assistants and RAG frontends — conversational access to accounts, transactions, and portfolios.',
        prompt: 'What AI-powered and RAG interfaces has Vamsi built?',
      },
      {
        text: 'His enterprise design system cut code duplication by 35% across digital banking and payments products.',
        prompt: 'Tell me about the design system Vamsi built.',
      },
      {
        text: 'Code splitting, lazy loading, and caching — page loads down 40% on customer-facing banking apps.',
        prompt: 'How did Vamsi improve frontend performance?',
      },
      {
        text: 'He holds a STEM M.S. in Business Analytics from Hult in Boston, and is U.S. work authorized with no sponsorship needed.',
        prompt: "What is Vamsi's educational background and work authorization?",
      },
      {
        text: "I'm not a recording — this chat runs on Claude, briefed on Vamsi's full résumé. Ask me anything.",
        prompt: 'What can you tell me about Vamsi in one paragraph?',
      },
      {
        text: 'The cursor on this site is a drop of living ink — it stretches with speed and morphs over anything clickable.',
      },
      {
        text: 'There is a full-page edition of this chat at /ai, typeset like a printed interview.',
      },
    ],
  },
}

export default avatar2
